import { Link } from "react-router-dom";
import { useSiteSettings, ArtistName } from "../context/SiteSettingsContext";

export default function Footer() {
  const { settings, nav, profile } = useSiteSettings();
  const socials = (settings?.social || []).filter((s) => s.label && s.url);
  const copyright = profile.copyrightText || settings?.footerCopy || "© MOTION & MEANING — ALL DIMENSIONS RESERVED";

  const dest = (it) => (it.route === "CUSTOM" ? it.customUrl || "#" : it.route);
  const external = (it) => it.newTab || /^https?:\/\//.test(dest(it));

  return (
    <footer className="bg-[#050505] border-t border-white/10" data-testid="main-footer">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-20">
          <div>
            <Link to="/join" data-testid="footer-join-cta" className="group inline-block">
              <p className="font-mono text-[11px] tracking-[0.25em] text-gold mb-4">MAILING LIST</p>
              <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] text-white group-hover:text-gold transition-colors duration-500">
                Enter the<br />Inner Circle
              </h2>
              <span className="inline-block mt-6 font-mono text-[11px] tracking-[0.2em] text-white/50 group-hover:text-gold transition-colors">
                → JOIN NOW
              </span>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-5">NAVIGATE</p>
              <ul className="space-y-3">
                {nav.map((it) => (
                  <li key={it.id || it.title}>
                    {external(it) ? (
                      <a href={dest(it)} target={it.newTab ? "_blank" : undefined} rel="noreferrer"
                        className="text-sm text-white/70 hover:text-gold transition-colors"
                        data-testid={`footer-link-${(it.title || "").toLowerCase().replace(/\s+/g, "-")}`}>
                        {it.title}
                      </a>
                    ) : (
                      <Link to={dest(it)} className="text-sm text-white/70 hover:text-gold transition-colors"
                        data-testid={`footer-link-${(it.title || "").toLowerCase().replace(/\s+/g, "-")}`}>
                        {it.title}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-5">CONNECT</p>
              <ul className="space-y-3">
                {socials.length === 0 && <li className="text-sm text-white/30">—</li>}
                {socials.map((s) => (
                  <li key={s.label}>
                    <a href={s.url} target="_blank" rel="noreferrer"
                      className="text-sm text-white/70 hover:text-gold transition-colors"
                      data-testid={`footer-social-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {s.label}
                    </a>
                  </li>
                ))}
                {profile.showContactEmail && profile.contactEmail && (
                  <li>
                    <a href={`mailto:${profile.contactEmail}`} data-testid="footer-contact-email"
                      className="text-sm text-gold/80 hover:text-gold transition-colors break-all">
                      {profile.contactEmail}
                    </a>
                  </li>
                )}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-5">ACCOUNT</p>
              <ul className="space-y-3">
                <li><Link to="/account" className="text-sm text-white/70 hover:text-gold transition-colors" data-testid="footer-link-account">My Account</Link></li>
                <li><Link to="/register" className="text-sm text-white/70 hover:text-gold transition-colors" data-testid="footer-link-claim">Claim Guest Orders</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="gold-line mb-8" />
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <span className="font-serif text-2xl tracking-[0.2em]"><ArtistName name={profile.displayName} /></span>
            {profile.shortBio && (
              <p className="text-white/40 text-sm mt-3 max-w-md leading-relaxed" data-testid="footer-short-bio">{profile.shortBio}</p>
            )}
          </div>
          <span className="font-mono text-[10px] tracking-[0.2em] text-white/40" data-testid="footer-copyright">{copyright.toUpperCase()}</span>
        </div>
      </div>
    </footer>
  );
}
